import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocalStorage } from '@uidotdev/usehooks';
import Loader from '@atoms/Atom/Loader';
import { useRouter } from 'next/router';
import {
  isInReadMode,
  isPowerPointDesktop,
  isPowerPointOnline,
  waitForPowerpoint
} from '@services/content-add-in-services';
import { NotSupportedPage } from './RunOfShow/Powerpoint/NotSupportedPage';
import ResizeHint from './RunOfShow/Powerpoint/ResizeHint';
import { useOfficeStore } from '@store/officeStore';
import { useShallow } from 'zustand/react/shallow';
import { debugLog, probeIndexedDb } from '@services/debug-log.service';
import { logToCloudWatch } from '@services/logger.service';
import { reconcileSlideIdentity, syncInteractionRef } from '@services/deck-identity.service';

const todayDateString = () => new Date().toLocaleDateString('en-CA');

export const useOffice = () => useOfficeStore(useShallow(s => ({
  officeReady: s.officeReady,
  officeError: s.officeError,
  isLoading: s.isLoading,
  isPowerpoint: s.isPowerpoint,
  activeView: s.activeView,
  isPowerpointDesktop: s.isPowerpointDesktop,
  getLastRoutePath: s.getLastRoutePath,
  setIsPowerpointDesktop: s.setIsPowerpointDesktop,
  setIsPowerpointOnline: s.setIsPowerpointOnline,
  setIsPowerpoint: s.setIsPowerpoint,
})));

export default function OfficeLoader() {
  const {
    isPowerpoint,
    isPowerpointOnline,
    officeReady,
    activeView,
    setIsPowerpointDesktop,
    setIsPowerpointOnline,
    setOfficeReady,
    setOfficeError,
    setIsLoading,
    setActiveView,
  } = useOfficeStore(useShallow(s => ({
    isPowerpoint: s.isPowerpoint,
    isPowerpointOnline: s.isPowerpointOnline,
    officeReady: s.officeReady,
    activeView: s.activeView,
    setIsPowerpointDesktop: s.setIsPowerpointDesktop,
    setIsPowerpointOnline: s.setIsPowerpointOnline,
    setOfficeReady: s.setOfficeReady,
    setOfficeError: s.setOfficeError,
    setIsLoading: s.setIsLoading,
    setActiveView: s.setActiveView,
  })));

  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const originalPushState = useRef<typeof window.history.pushState | null>(null);
  const originalReplaceState = useRef<typeof window.history.replaceState | null>(null);
  const [resizeHintDontShowAgain, setResizeHintDontShowAgain] = useLocalStorage('sa_ppt_resize_hint_dont_show_again', false);
  const [resizeHintLastDismissedDate, setResizeHintLastDismissedDate] = useLocalStorage('sa_ppt_resize_hint_last_dismissed_date', '');
  const resizeHintDismissedToday = resizeHintLastDismissedDate === todayDateString();

  // Route transitions for /debug/powerpoint: where we were -> where we ended up.
  useEffect(() => {
    if (!isPowerpoint) return;
    const events = routerRef.current.events;
    let from = routerRef.current.asPath;
    const onComplete = (url: string) => {
      debugLog('route', `${from} -> ${url}`, { before: from, after: url });
      from = url;
    };
    const onError = (err: unknown, url: string) => {
      debugLog('error', `route failed: ${from} -> ${url}`, {
        before: from,
        after: url,
        error: String((err as Error)?.message ?? err),
        cancelled: Boolean((err as { cancelled?: boolean })?.cancelled)
      });
    };

    debugLog('route', `initial: ${from}`, { after: from });
    events.on('routeChangeComplete', onComplete);
    events.on('routeChangeError', onError);
    return () => {
      events.off('routeChangeComplete', onComplete);
      events.off('routeChangeError', onError);
    };
  }, [isPowerpoint]);

  useEffect(() => {
    if (!isPowerpoint || !officeReady) return;
    debugLog('office', 'office ready', {
      host: window.Office?.context?.host,
      platform: window.Office?.context?.platform,
      activeView
    });
    probeIndexedDb();
  }, [isPowerpoint, officeReady, activeView]);

  useEffect(() => {
    if (!isPowerpoint) return;

    isPowerPointDesktop().then((isDesktop: boolean) => {
      setIsPowerpointDesktop(isDesktop);
    }).catch((error: unknown) => {
      console.error('Error detecting platform:', error);
      setIsPowerpointDesktop(false);
    });

    isPowerPointOnline().then((isOnline: boolean) => {
      setIsPowerpointOnline(isOnline);
    }).catch((error: unknown) => {
      console.error('Error detecting platform:', error);
      setIsPowerpointOnline(false);
    });
  }, [isPowerpoint]);

  useEffect(() => {
    if (!isPowerpoint) return;

    const initializeOffice = async () => {
      try {
        if (!window.Office) {
          originalPushState.current = window.history.pushState?.bind(window.history);
          originalReplaceState.current = window.history.replaceState?.bind(window.history);
          await loadOfficeJs();
          if (originalPushState.current) {
            window.history.pushState = originalPushState.current;
          }
          if (originalReplaceState.current) {
            window.history.replaceState = originalReplaceState.current;
          }
        }

        await waitForPowerpoint();

        const host = window.Office?.context?.host;
        const supportedHosts = [Office.HostType.PowerPoint];

        if (host && supportedHosts.includes(host)) {
          await setupDocumentHandlers();
        }

        setOfficeReady(true);
      } catch (error) {
        console.error('Office initialization error:', error);
        setOfficeError(error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeOffice();
  }, [isPowerpoint]);

  const loadOfficeJs = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://appsforoffice.microsoft.com/lib/1/hosted/Office.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Office.js'));
      document.head.appendChild(script);
    });
  };

  const setupDocumentHandlers = async (): Promise<void> => {
    if (!window.Office?.context?.document) {
      console.warn('Document API not available in current host');
      return;
    }

    const isReadMode = await isInReadMode();
    setActiveView(isReadMode ? Office.ActiveView.Read : Office.ActiveView.Edit);
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      window.Office?.context.document.goToByIdAsync(Office.Index.Next, Office.GoToType.Index);
    }
    if (event.key === 'ArrowUp') {
      window.Office?.context.document.goToByIdAsync(Office.Index.Previous, Office.GoToType.Index);
    }
  };

  useEffect(() => {
    if (!isPowerpoint) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPowerpoint]);

  useEffect(() => {
    if (!isPowerpoint || !officeReady || activeView !== Office.ActiveView.Edit) return;

    const saveCurrentRoute = async (): Promise<void> => {
      try {
        if (!window.Office?.context?.document?.settings) return;
        const isReadMode = await isInReadMode();
        if (isReadMode) return;
        if (['/login', '/signup', '/', '/entry/powerpoint', '/debug/powerpoint'].includes(router.pathname)) return;
        const lastRoute = router.asPath;
        if (!lastRoute) {
          logToCloudWatch('[Powerpoint]: null value for lastRoute', {
            path: router.pathname
          });
          return;
        }
        await useOfficeStore.getState().saveSettings('lastRoute', lastRoute);
        await syncInteractionRef(lastRoute);
      } catch (error) {
        logToCloudWatch('[Powerpoint]: Error saving current route', {
          path: router.pathname,
          lastRoute: router.asPath
        });
        console.error('Error saving current route:', error);
      }
    };

    saveCurrentRoute();
  }, [router.pathname, router.query, officeReady, isPowerpoint, activeView]);

  // Duplicating a slide selects the copy without reloading the add-in, so re-check identity on
  // every slide change and clone the interaction if the new slide is a fork of ours.
  useEffect(() => {
    if (!isPowerpoint || !officeReady || activeView !== Office.ActiveView.Edit) return;
    const document = window.Office?.context?.document;
    if (!document) return;

    let running = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = async () => {
      if (running) return;
      running = true;
      try {
        if (['/login', '/signup', '/', '/entry/powerpoint', '/debug/powerpoint'].includes(routerRef.current.pathname)) return;
        const lastRoute = await useOfficeStore.getState().getLastRoutePath();
        const { route, changed } = await reconcileSlideIdentity(lastRoute, { forkOnly: true });
        if (changed) await routerRef.current.push(route);
      } catch (error) {
        console.error('[deck-identity] slide change check failed:', error);
      } finally {
        running = false;
      }
    };
    const handleSlideChange = () => {
      clearTimeout(timer);
      timer = setTimeout(check, 300);
    };

    document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, handleSlideChange);
    return () => {
      clearTimeout(timer);
      document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, { handler: handleSlideChange });
    };
  }, [isPowerpoint, officeReady, activeView]);

  if (!isPowerpoint) return null;
  if (isPowerpointOnline) return createPortal(<NotSupportedPage />, document.body);

  const canRenderContent =
    officeReady &&
    (activeView === Office.ActiveView.Edit || activeView === Office.ActiveView.Read);

  const showResizeHint =
    canRenderContent &&
    activeView === Office.ActiveView.Edit &&
    !resizeHintDontShowAgain &&
    !resizeHintDismissedToday;

  if (canRenderContent) {
    if (!showResizeHint) return null;

    return createPortal(
      <ResizeHint
        isOpen={showResizeHint}
        onDismiss={(dontShowAgain: boolean) => {
          if (dontShowAgain) setResizeHintDontShowAgain(true);
          setResizeHintLastDismissedDate(todayDateString());
        }}
      />,
      document.body
    );
  }

  return createPortal(
    <div className="h-screen fixed inset-0 z-50">
      <Loader isPowerpoint />
    </div>,
    document.body
  );
}
